export type ImageUploadProfile = "standard" | "hd" | "location" | "physweb" | "historical";

const SUPPORTED_IMAGE_EXTENSIONS_RE = /\.(jpe?g|webp|png)$/i;
const HEIC_EXTENSION_RE = /\.(heic|heif)$/i;
const HEIC_MIME_RE = /heic|heif/i;

/** Client-side targets for UX. Server re-encodes aurora uploads in submitAuroraReport. */
export const IMAGE_UPLOAD_PROFILES: Record<
    ImageUploadProfile,
    { maxDimension: number; quality: number; maxBytes: number; label: string }
> = {
    standard: {
        maxDimension: 2048,
        quality: 0.82,
        maxBytes: 2.5 * 1024 * 1024,
        label: "WebP (optimized)",
    },
    hd: {
        maxDimension: 4096,
        quality: 0.88,
        maxBytes: 4 * 1024 * 1024,
        label: "WebP (HD)",
    },
    location: {
        maxDimension: 1600,
        quality: 0.8,
        maxBytes: 1.5 * 1024 * 1024,
        label: "WebP (location)",
    },
    physweb: {
        maxDimension: 2048,
        quality: 0.85,
        maxBytes: 2.5 * 1024 * 1024,
        label: "WebP (Mount John all-sky camera)",
    },
    historical: {
        maxDimension: 2048,
        quality: 0.85,
        maxBytes: 4 * 1024 * 1024,
        label: "WebP (historical archive)",
    },
};

export class ImageProcessingError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "ImageProcessingError";
    }
}

export function getWebPFileName(fileName: string): string {
    const withoutExtension = fileName.replace(/\.[^.]*$/, "");
    return `${withoutExtension || "image"}.webp`;
}

/** DSLR JPEGs often have an empty MIME type; accept by extension too. */
export function isBrowserProcessableImage(file: { name: string; type?: string }): boolean {
    if (isHeicImage(file)) {
        return false;
    }
    const mime = file.type?.trim();
    if (mime !== undefined && mime.length > 0 && mime.startsWith("image/")) {
        return true;
    }
    return SUPPORTED_IMAGE_EXTENSIONS_RE.test(file.name);
}

function isHeicImage(file: { name: string; type?: string }): boolean {
    return HEIC_EXTENSION_RE.test(file.name) || HEIC_MIME_RE.test(file.type?.trim() ?? "");
}

/** Null when the file can be converted in the browser. */
export function imageSelectionErrorMessage(file: { name: string; type?: string }): string | null {
    if (isHeicImage(file)) {
        return "HEIC photos can't be uploaded. Export the photo as JPEG or PNG and try again.";
    }
    if (!isBrowserProcessableImage(file)) {
        return "Choose a JPEG, PNG, or WebP image.";
    }
    return null;
}

function loadImageElement(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new ImageProcessingError("Could not process the image."));
        };
        image.src = objectUrl;
    });
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
    return new Promise((resolve) => {
        canvas.toBlob(resolve, "image/webp", quality);
    });
}

function blobToFile(blob: Blob, fileName: string, lastModified: number): File {
    return new File([blob], fileName, {
        type: "image/webp",
        lastModified,
    });
}

async function encodeImageToWebP(
    image: HTMLImageElement,
    maxDimension: number,
    quality: number,
): Promise<{ blob: Blob; width: number; height: number }> {
    const scale = Math.min(
        1,
        maxDimension / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height),
    );
    const width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
    const height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
        throw new ImageProcessingError("Could not process the image.");
    }

    context.drawImage(image, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, quality);
    if (!blob) {
        throw new ImageProcessingError("Could not convert the image to WebP.");
    }

    return { blob, width, height };
}

async function compressToTargetSize(
    image: HTMLImageElement,
    profile: ImageUploadProfile,
    fileName: string,
    lastModified: number,
): Promise<File> {
    const { maxDimension, quality, maxBytes } = IMAGE_UPLOAD_PROFILES[profile];
    let dimension = maxDimension;
    let currentQuality = quality;

    for (let attempt = 0; attempt < 8; attempt += 1) {
        const { blob } = await encodeImageToWebP(image, dimension, currentQuality);
        if (blob.size <= maxBytes) {
            return blobToFile(blob, fileName, lastModified);
        }

        if (currentQuality > 0.55) {
            currentQuality = Math.max(0.55, currentQuality - 0.1);
            continue;
        }

        dimension = Math.max(1280, Math.round(dimension * 0.85));
    }

    throw new ImageProcessingError(
        "Image is too large even after compression. Try a smaller photo or crop before uploading.",
    );
}

export async function convertImageFileToWebP(
    file: File,
    profile: ImageUploadProfile = "standard",
): Promise<File> {
    const selectionError = imageSelectionErrorMessage(file);
    if (selectionError && typeof document !== "undefined") {
        throw new ImageProcessingError(selectionError);
    }

    if (
        typeof document === "undefined" ||
        typeof Image === "undefined" ||
        !isBrowserProcessableImage(file)
    ) {
        return file;
    }

    const image = await loadImageElement(file);
    return compressToTargetSize(image, profile, getWebPFileName(file.name), file.lastModified);
}

/** Convert a historical admin upload to WebP before Convex storage (EXIF must be read first). */
export async function convertHistoricalImageToWebP(file: File): Promise<File> {
    return convertImageFileToWebP(file, "historical");
}

export function auroraUploadProfile(hasProAccess: boolean): ImageUploadProfile {
    return hasProAccess ? "hd" : "standard";
}

function sanitizeDownloadFileName(fileName: string): string {
    const trimmed = fileName.trim() || "image.webp";
    return trimmed.replace(/[/\\?%*:|"<>]/g, "-");
}

/** Fetch an image URL in the browser and trigger a download with a WebP filename. */
export async function downloadImageInBrowser(
    imageUrl: string,
    fileName = "image.webp",
): Promise<void> {
    if (typeof document === "undefined") {
        throw new ImageProcessingError("Downloads are only available in the browser.");
    }

    const response = await fetch(imageUrl);
    if (!response.ok) {
        throw new ImageProcessingError("Could not download the image.");
    }

    const blob = await response.blob();
    const downloadName = sanitizeDownloadFileName(
        fileName.endsWith(".webp") ? fileName : `${fileName.replace(/\.[^.]*$/, "")}.webp`,
    );
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = downloadName;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
}
